/** @typedef {import('headlight-configurable-application').Pict} Pict */

const MaterialsEntityInputType = require('./PictProvider-InputType-MaterialsEntitySelector.js');
const ScopedEntityData = require('./_ScopedEntityData.js');

class MaterialsSampleInputType extends MaterialsEntityInputType
{
	constructor(pFable, pOptions, pServiceHash)
	{
		if (!pOptions)
		{
			pOptions = { };
		}
		pOptions.Entity = 'Sample'; //TODO: use default options for this?
		pOptions.AllowEntityCreation = true; // can turn off via PictForm option of same name
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
	}

	/**
	 * @param {Object} pInput
	 *
	 * @return {string}
	 */
	_getDefaultRecordIdentity(pInput)
	{
		return 'GUIDSample';
	}

	/**
	 * @param {Object} pView
	 * @param {Object} pInput
	 * @param {number|string} [pRowIndex]
	 */
	_getDefaultRecord(pView, pInput, pRowIndex)
	{
		let tmpRecord = super._getDefaultRecord(pView, pInput, pRowIndex);
		if (!tmpRecord)
		{
			//FIXME: need to get some kind of record uniqueness injected here
			tmpRecord = { GUIDSample: `{~D:AppData.DocumentData.ReportData.GUIDDocument~}-S-${this._getSalt(pView, pInput, pRowIndex)}` };
		}
		if (this.pict.AppData.DocumentData?.ReportData?.IDProject > 0)
		{
			tmpRecord.IDProject = this.pict.AppData.DocumentData.ReportData.IDProject;
		}
		tmpRecord.CreatingIDUser = this.pict.AppData.CurrentUser?.UserID;
		tmpRecord.DesignatedTime = new Date().toISOString();
		tmpRecord.QRCode = this.pict.providers.QRCodeProvider.generateNewQRCode(tmpRecord);
		return tmpRecord;
	}

	/**
	 * Opt-in via PictForm.DisabledOnceSaved: disable this selector once it has a value AND the document
	 * has been persisted, so a saved selection can't be swapped. A brand-new (unsaved) form stays editable.
	 * Sets PictForm.Disabled, which the template honors for both the <select> and the advanced
	 * Sample-Search-Panel button. Distinct from the document-level lock (ReportData.Locked), which freezes
	 * the whole form via the Read theme. A selector that is never editable sets PictForm.Disabled directly.
	 *
	 * @param {Object} pInput - The input descriptor.
	 * @param {any} pValue - The current value (the selected sample's GUID, when one exists).
	 */
	_applyDisabledOnceSaved(pInput, pValue)
	{
		if (!pInput || !pInput.PictForm || !pInput.PictForm.DisabledOnceSaved)
		{
			return;
		}
		const tmpDocumentSaved = this.pict?.AppData?.DocumentData?.ReportData?.IDDocument > 0;
		if (pValue && tmpDocumentSaved)
		{
			pInput.PictForm.Disabled = true;
		}
	}

	/**
	 * Initializes the input element for the Pict provider select input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {boolean} - Returns true if the input element is successfully initialized, false otherwise.
	 */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		if (!pInput.PictForm)
		{
			pInput.PictForm = { };
		}
		if (pInput.PictForm.EntityTag == null)
		{
			pInput.PictForm.EntityTag = 'QRCode';
		}
		// Opt-in via PictForm.DisabledOnceSaved: a saved sample can't be swapped, a blank new form can.
		this._applyDisabledOnceSaved(pInput, pValue);
		//FIXME: this seems to assume there will only be a single sample selector on the page at a time................
		//       this should be wired up only when the user clicks the button to open the panel.
		// set the Sample selection callback for the Sample Search Panel
		this.pict.views.HeadlightPaneSamples.setSampleSelectionCallback((pSample) =>
		{
			this.pict.log.info('Sample selected:', pSample.GUIDSample);
			this.pict.log.info('Assigning Sample to the following input element:', pInput.Macro.RawHTMLID);
			this.pict.AppData.SelectedSample = pSample;
			document.getElementById(pInput.Macro.RawHTMLID)['value'] = pSample.GUIDSample;
			document.getElementById(pInput.Macro.RawHTMLID).dispatchEvent(new Event('change'));
			//FIXME: consolidate this with the base class..........
			// update the select2 element to show the selected sample
			const tmpSelectElement = this.pict.ContentAssignment.getElement(this.getSelectInputHTMLID(pInput.Macro.RawHTMLID))[0];
			const tmpOption = document.createElement('option');
			tmpOption.value = pSample[this._getRecordIdentity(pInput)];
			tmpOption.text = this._formatRecordForDisplay(pSample, pInput);
			tmpOption.selected = true;
			// remove any existing options
			while (tmpSelectElement.firstChild)
			{
				tmpSelectElement.removeChild(tmpSelectElement.firstChild);
			}
			tmpSelectElement.appendChild(tmpOption);
			// trigger change to update select2
			$(tmpSelectElement).trigger('change');
		});
		// sample is in charge of the family for tracking
		if (pInput.PictForm.SampleTrackingGUID && pInput.PictForm.SampleTrackingGUID != true)
		{
			this._setTrackingGUID(pInput, null, pInput.PictForm.SampleTrackingGUID);
			return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
		}
		let tmpTrackingGUID = this._getTrackingGUID(pInput);
		if (!tmpTrackingGUID)
		{
			tmpTrackingGUID = this.pict.getUUID();
			this._setTrackingGUID(pInput, null, tmpTrackingGUID);
			pInput.PictForm.SampleTrackingGUID = tmpTrackingGUID;
		}
		return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Initializes a tabular input element.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The index of the row.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {any} - The result of the initialization.
	 */
	onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		if (!pInput.PictForm)
		{
			pInput.PictForm = { };
		}
		if (pInput.PictForm.EntityTag == null)
		{
			pInput.PictForm.EntityTag = 'QRCode';
		}
		// Honor PictForm.DisabledOnceSaved for tabular sample selectors too (see onInputInitialize).
		this._applyDisabledOnceSaved(pInput, pValue);
		if (pInput.PictForm.SampleTrackingGUID && pInput.PictForm.SampleTrackingGUID != true)
		{
			this._setTrackingGUID(pInput, pRowIndex, pInput.PictForm.SampleTrackingGUID);
			return super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
		}
		let tmpTrackingGUID = this._getTrackingGUID(pInput, pRowIndex);
		if (!tmpTrackingGUID)
		{
			tmpTrackingGUID = this.pict.getUUID();
			this._setTrackingGUID(pInput, pRowIndex, tmpTrackingGUID);
			pInput.PictForm.SampleTrackingGUID = true; // different rows will have different values so avoid confusion
		}
		return super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * Handles events for the Pict-Provider-InputExtension.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value from AppData.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pEvent - The event hash that is expected to be triggered.
	 * @param {string} pTransactionGUID - The transaction GUID, if any.
	 * @returns {boolean} - Returns true.
	 */
	onAfterEventCompletion(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID)
	{
		this._handleEvent(pView, pInput, pValue, pHTMLSelector, null, pEvent, pTransactionGUID);
		return super.onAfterEventCompletion(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID);
	}

	/**
	 * Handles events for the Pict-Provider-InputExtension.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value from AppData.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The row index of the tabular data.
	 * @param {string} pEvent - The event hash that is expected to be triggered.
	 * @param {string} pTransactionGUID - The transaction GUID, if any.
	 * @returns {boolean} - Returns true.
	 */
	onAfterEventTabularCompletion(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID)
	{
		this._handleEvent(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID);
		return super.onAfterEventTabularCompletion(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID);
	}

	/**
	 * Save-lifecycle hook: stamp naming + numbering (Name, LabControlNumber, Sequenced) on the
	 * Sample record BEFORE the base class projects it into _EntityData (super), so the projected
	 * copy carries the stamped values. Delegates to the SampleSequencing provider; idempotent and
	 * fail-safe, so a stamp failure never blocks the save.
	 *
	 * @param {(pError?: Error) => void} fCallback
	 * @return {void}
	 */
	onBeforeSaveDataAsync(fCallback)
	{
		const tmpProvider = this.pict.providers?.SampleSequencing;
		if (!tmpProvider || typeof tmpProvider.applyBeforeSave !== 'function')
		{
			return super.onBeforeSaveDataAsync(fCallback);
		}
		tmpProvider.applyBeforeSave().then(() => super.onBeforeSaveDataAsync(fCallback)).catch((pError) =>
		{
			this.pict.log.error(`SampleSequencing stamp failed before save (continuing): ${pError?.message || pError}`);
			super.onBeforeSaveDataAsync(fCallback);
		});
	}

	/**
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {Object} pRecord - The record that was refreshed.
	 * @param {string} pRecordAddress - The address where the record was stored.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @param {number} [pRowIndex] - The row index. If present, this is a tabular input.
	 */
	_onRecordRefresh(pView, pInput, pRecord, pRecordAddress, pTransactionGUID, pRowIndex)
	{
		const tmpSampleAddress = this._getRecordAddress(pView, pInput, pRowIndex);
		let tmpSample = pRecord;
		if (!tmpSample || typeof tmpSample !== 'object' || Array.isArray(tmpSample))
		{
			tmpSample = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleAddress);
		}
		if (tmpSample && typeof tmpSample === 'object' && Object.keys(tmpSample).length === 0)
		{
			tmpSample = null;
		}
		const tmpSampleDocumentPolyJoinAddress = pInput.PictForm.DocumentPolyJoinAddress || `${tmpSampleAddress}_DocumentPolyJoin`;
		let tmpDocumentPolyJoin = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleDocumentPolyJoinAddress);
		if (ScopedEntityData.referencesSample(tmpSample))
		{
			// A held join with no RemoteType is a stub left by a clear; rebuild it rather than stamp it.
			if (!tmpDocumentPolyJoin || typeof tmpDocumentPolyJoin !== 'object' || !tmpDocumentPolyJoin.RemoteType)
			{
				tmpDocumentPolyJoin = JSON.parse(JSON.stringify(pInput.PictForm.DefaultDocumentPolyJoinRecord ||
					{
						GUIDDocumentPolyJoin: `{~D:AppData.DocumentData.ReportData.GUIDDocument~}-S_DPJ-${this._getSalt(pView, pInput, pRowIndex)}`,
						RemoteType: 'Sample',
						IDDocument: `{~D:AppData.DocumentData.ReportData.IDDocument~}`,
						GUIDDocument: `{~D:AppData.DocumentData.ReportData.GUIDDocument~}`,
						IDRemote: tmpSample.IDSample,
					}));
				if (tmpDocumentPolyJoin && typeof tmpDocumentPolyJoin === 'object')
				{
					this._translateRecordTemplateFields(tmpDocumentPolyJoin);
				}
			}
			if (!tmpDocumentPolyJoin.GUIDDocumentPolyJoin)
			{
				tmpDocumentPolyJoin.GUIDDocumentPolyJoin = this.pict.getUUID();
			}
			const tmpDocument = this.pict.AppData.DocumentData.ReportData;
			if (tmpDocument)
			{
				if (tmpDocument.IDDocument > 0)
				{
					tmpDocumentPolyJoin.IDDocument = tmpDocument.IDDocument;
					delete tmpDocumentPolyJoin.GUIDDocument;
				}
				else
				{
					tmpDocumentPolyJoin.GUIDDocument = tmpDocument.GUIDDocument;
					delete tmpDocumentPolyJoin.IDDocument;
				}
			}
			if (!tmpDocumentPolyJoin.IDRemote)
			{
				if (tmpSample.IDSample)
				{
					tmpDocumentPolyJoin.IDRemote = tmpSample.IDSample;
				}
				else
				{
					tmpDocumentPolyJoin.GUIDSample = tmpSample.GUIDSample;
					tmpDocumentPolyJoin._Dest_GUIDSample = 'IDRemote';
					delete tmpDocumentPolyJoin.IDRemote;
				}
			}
		}
		else if (tmpDocumentPolyJoin && !tmpSample)
		{
			tmpDocumentPolyJoin = { };
		}
		//NOTE: need to set this here in case it is used in the TestInstanceSampleJoin default record template
		this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleDocumentPolyJoinAddress, tmpDocumentPolyJoin);
		//TOOD: is this a good idea? this ensures any fields that use this record are updated immediately, if they are in the same section
		pView.marshalToView();
		super._onRecordRefresh(pView, pInput, pRecord, pRecordAddress, pTransactionGUID, pRowIndex);
		// Refresh spec-year scope from the sample's project. Resolver dedupes
		// when the project hasn't changed; fail-open paths inside the resolver
		// handle bare contracts / empty stub sets / no spec infra. Triggered
		// here (rather than via PictFormMetacontroller's global event channel,
		// which only routes to views) because providers don't receive those
		// events. See ~/dev/material_selectors_spec_year_audit.md §10.7 for
		// the wiring rationale.
		this.pict.providers.SpecScopeResolver?.loadForSampleRecord?.(tmpSample);
		// Live-refresh the auto-generated Sample Name from the current source fields (project / material /
		// date). Guarded so it only regenerates while the user hasn't edited the (now editable) Name field —
		// so it shows and tracks the fields early, yet a manual edit is never clobbered. Same bridge rationale
		// as SpecScopeResolver above: providers don't receive the form's global events.
		if (this.pict.providers.SampleSequencing?.refreshSampleName?.())
		{
			pView.marshalToView();
		}
		this.pict.providers.DataRequirementsProvider.applyDataRequirements(this.pict.views.PictFormMetacontroller.manifestDescription, pRecord).catch((pError) =>
		{
			this.pict.log.error(`Error applying data requirements after sample refresh: ${pError.message || pError}`);
		});
	}


	/**
	 * @param {Object} pView
	 * @param {Object} pInput
	 * @param {any} pValue
	 * @param {string} pHTMLSelector
	 * @param {number|string|null} pRowIndex
	 * @param {string} pEvent
	 * @param {string} pTransactionGUID
	 */
	_handleEvent(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID)
	{
		if (!pEvent || typeof pEvent !== 'string')
		{
			return;
		}

		const [ tmpEventType, tmpTrackingGUID, tmpEventPayload ] = pEvent.split(':');

		if (tmpEventType === 'ProjectEntityDataRecords')
		{
			const tmpTarget = this._getProjectionTarget(pInput, pEvent);
			if (!tmpTarget)
			{
				return;
			}
			this._syncRecordToEntityData(pView, pInput, pRowIndex, pTransactionGUID, tmpTarget);
			const tmpSampleAddress = this._getRecordAddress(pView, pInput, pRowIndex);
			const tmpDocumentPolyJoinAddress = pInput.PictForm.DocumentPolyJoinAddress || `${tmpSampleAddress}_DocumentPolyJoin`;
			const tmpDocumentPolyJoin = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpDocumentPolyJoinAddress);
			if (tmpDocumentPolyJoin && typeof tmpDocumentPolyJoin === 'object' && !Array.isArray(tmpDocumentPolyJoin) && tmpDocumentPolyJoin.GUIDDocumentPolyJoin)
			{
				if (!tmpTarget.DocumentPolyJoin || typeof tmpTarget.DocumentPolyJoin !== 'object' || Array.isArray(tmpTarget.DocumentPolyJoin))
				{
					tmpTarget.DocumentPolyJoin = { };
				}
				tmpTarget.DocumentPolyJoin[tmpDocumentPolyJoin.GUIDDocumentPolyJoin] = tmpDocumentPolyJoin;
			}

			return;
		}
		// global events above here; below are targetted events
		if (tmpTrackingGUID !== this._getTrackingGUID(pInput, pRowIndex))
		{
			return;
		}
		if (tmpEventType === 'SampleStatusChanged')
		{
			const tmpRecordAddress = this._getRecordAddress(pView, pInput, pRowIndex);
			const tmpRecord = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpRecordAddress);
			tmpRecord.Status = tmpEventPayload;
			return;
		}

		if (tmpEventType === 'PushRecord')
		{
			const tmpSampleAddress = this._getRecordAddress(pView, pInput, pRowIndex);
			const tmpSampleRecord = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpSampleAddress);
			this.pict.manifest.setValueAtAddress(this.pict, tmpEventPayload, tmpSampleRecord);
		}
	}

	/**
	 * @param {Record<string, any>} pRecord
	 * @param {Object} pInput
	 *
	 * @return {string}
	 */
	_getRecordDisplayName(pRecord, pInput)
	{
		if (!pRecord || typeof pRecord !== 'object' || Array.isArray(pRecord))
		{
			return '#INVALID#';
		}
		let tmpName = pRecord.Name;
		if (!pRecord.Name)
		{
			if (!pRecord.LabControlNumber)
			{
				// A synthesized (not-yet-saved) sample has no IDSample; show "New Sample" rather than "#??".
				tmpName = pRecord.IDSample ? `Unnamed Sample #${pRecord.IDSample}` : 'New Sample';
			}
			else
			{
				tmpName = `LCN: ${pRecord.LabControlNumber}`;
			}
		}
		return tmpName;
	}

	/**
	 * @param {import('./PictProvider-Select2Util.js').Select2AjaxOptions} pConfigOptions
	 * @param {Object} pView
	 * @param {Object} pInput
	 * @param {number} [pRowIndex]
	 */
	_updateSelect2Options(pConfigOptions, pView, pInput, pRowIndex)
	{
		super._updateSelect2Options(pConfigOptions, pView, pInput, pRowIndex);
		pConfigOptions.SearchFields = pConfigOptions.SearchFields || [ 'QRCode', 'Name' ];
	}
}

module.exports = MaterialsSampleInputType;
// Opt into the app save-data lifecycle so onBeforeSaveDataAsync (the SampleSequencing stamp, run
// before the _EntityData projection sweep) actually fires. NOT a framework default — sibling
// selectors that project records on save (Test, SampleTestResult) set this too.
module.exports.default_configuration = { AutoSaveDataWithApp: true };
